import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import usePageTitle from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronLeft, Trash2, RotateCcw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const APPARATUS_LIST = [
  { value: 'aa', label: 'AA (All Around)' },
  { value: 'vault', label: 'Vault' },
  { value: 'bars', label: 'Bars' },
  { value: 'beam', label: 'Beam' },
  { value: 'floor', label: 'Floor' },
  { value: 'range', label: 'Range & Conditioning' },
];

export default function RankOverridesPage({ user, onLogout }) {
  usePageTitle('Rank Overrides');
  const navigate = useNavigate();

  const [competitions, setCompetitions] = useState([]);
  const [selectedComp, setSelectedComp] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedApparatus, setSelectedApparatus] = useState('aa');
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  // Working edits: gymnastId -> rank value (string, to allow empty)
  const [edits, setEdits] = useState({});

  const token = localStorage.getItem('token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  // Load competitions on mount
  useEffect(() => {
    axios.get(`${API}/public/competitions`)
      .then(res => {
        const list = res.data.competitions || res.data || [];
        setCompetitions(Array.isArray(list) ? list : []);
      })
      .catch(() => toast.error('Failed to load competitions'));
  }, []);

  // Load categories when competition changes
  useEffect(() => {
    if (!selectedComp) {
      setCategories([]);
      setSelectedCategory('');
      return;
    }
    axios.get(`${API}/public/categories/${selectedComp}`)
      .then(res => {
        const cats = res.data.categories || [];
        setCategories(cats);
        if (cats.length > 0 && !cats.includes(selectedCategory)) {
          setSelectedCategory(cats[0]);
        }
      })
      .catch(() => toast.error('Failed to load categories'));
  }, [selectedComp]); // eslint-disable-line

  // Load rankings when filters change
  useEffect(() => {
    if (!selectedComp || !selectedCategory || !selectedApparatus) {
      setRankings([]);
      setEdits({});
      return;
    }
    loadRankings();
  }, [selectedComp, selectedCategory, selectedApparatus]); // eslint-disable-line

  const loadRankings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/public/rankings/${selectedComp}`,
        { params: { category: selectedCategory, apparatus: selectedApparatus } }
      );
      const rows = res.data.rankings || [];
      setRankings(rows);
      const seed = {};
      rows.forEach(r => {
        if (r.gymnastId) seed[r.gymnastId] = r.rank != null ? String(r.rank) : '';
      });
      setEdits(seed);
    } catch {
      toast.error('Failed to load rankings');
    } finally {
      setLoading(false);
    }
  };

  const handleEditChange = (gymnastId, value) => {
    if (value === '' || /^\d+$/.test(value)) {
      setEdits(prev => ({ ...prev, [gymnastId]: value }));
    }
  };

  const handleSave = async (row, overrideValue = undefined) => {
    const newRankStr = (overrideValue !== undefined ? overrideValue : (edits[row.gymnastId] ?? '')).trim();
    setSavingId(row.gymnastId);
    try {
      if (!newRankStr) {
        // Empty → remove any override so it returns to automatic rank
        await axios.delete(`${API}/superadmin/rank-overrides`, {
          ...authHeaders,
          params: {
            competition_id: selectedComp,
            category: selectedCategory,
            apparatus: selectedApparatus,
            gymnast_id: row.gymnastId,
          },
        });
        toast.success(`${row.name} — reverted to automatic rank`);
      } else {
        await axios.post(
          `${API}/superadmin/rank-overrides`,
          {
            competitionId: selectedComp,
            category: selectedCategory,
            apparatus: selectedApparatus,
            gymnastId: row.gymnastId,
            overrideRank: parseInt(newRankStr, 10),
          },
          authHeaders
        );
        toast.success(`${row.name} — rank set to ${newRankStr}`);
      }
      // Reload so UI reflects the new rank immediately
      await loadRankings();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Save failed: ${detail}`);
    } finally {
      setSavingId(null);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm(`Clear ALL rank overrides for "${selectedCategory}" (${selectedApparatus.toUpperCase()})? Rankings will return to automatic.`)) return;
    try {
      await axios.delete(`${API}/superadmin/rank-overrides/clear`, {
        ...authHeaders,
        params: {
          competition_id: selectedComp,
          category: selectedCategory,
          apparatus: selectedApparatus,
        },
      });
      toast.success('Overrides cleared for this apparatus');
      await loadRankings();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Clear failed: ${detail}`);
    }
  };

  const scoreOf = (r) => {
    if (selectedApparatus === 'aa') return r.aaScore;
    const s = r.score;
    if (!s || s.didNotCompete) return null;
    return s.finalScore;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/superadmin/dashboard')}
            data-testid="back-to-superadmin-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">Rank Overrides</h1>
            <p className="text-xs text-gray-500">
              Manually adjust rankings. Applies everywhere (Live Rankings, Presentations, PDF exports).
            </p>
          </div>
          {user && (
            <Button variant="outline" size="sm" onClick={onLogout}>Log out</Button>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 space-y-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Select what to edit</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Competition</Label>
              <Select value={selectedComp} onValueChange={setSelectedComp}>
                <SelectTrigger data-testid="rank-override-comp-select">
                  <SelectValue placeholder="Select competition..." />
                </SelectTrigger>
                <SelectContent>
                  {competitions.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category</Label>
              <Select
                value={selectedCategory}
                onValueChange={setSelectedCategory}
                disabled={!selectedComp}
              >
                <SelectTrigger data-testid="rank-override-cat-select">
                  <SelectValue placeholder="Select category..." />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Apparatus</Label>
              <Select value={selectedApparatus} onValueChange={setSelectedApparatus}>
                <SelectTrigger data-testid="rank-override-apparatus-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {APPARATUS_LIST.map(a => (
                    <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4 pb-2">
            <div>
              <CardTitle className="text-base">
                {selectedCategory || 'No category selected'}{' '}
                <span className="text-sm font-normal text-gray-500 ml-1">
                  • {APPARATUS_LIST.find(a => a.value === selectedApparatus)?.label}
                </span>
              </CardTitle>
              <p className="text-xs text-gray-500 mt-1">
                Type a new rank and press <strong>Save</strong>. Clear the field and Save to return to automatic rank.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearAll}
              disabled={!selectedCategory}
              className="text-red-600 hover:bg-red-50 border-red-200"
              data-testid="clear-all-overrides-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Clear all
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-gray-500 py-6 text-center">Loading…</p>
            ) : rankings.length === 0 ? (
              <p className="text-gray-500 py-6 text-center">
                {selectedCategory ? 'No gymnasts found.' : 'Pick a competition, category and apparatus.'}
              </p>
            ) : (
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 text-xs text-gray-700">
                    <tr>
                      <th className="text-left px-3 py-2 w-20">Current</th>
                      <th className="text-left px-3 py-2">Gymnast</th>
                      <th className="text-left px-3 py-2">Club</th>
                      <th className="text-right px-3 py-2 w-24">Score</th>
                      <th className="text-left px-3 py-2 w-64">New Rank</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rankings.map((r) => {
                      const overridden = r.rankOverridden;
                      const score = scoreOf(r);
                      const isSaving = savingId === r.gymnastId;
                      return (
                        <tr
                          key={r.gymnastId}
                          className={`border-t ${overridden ? 'bg-amber-50' : ''}`}
                          data-testid={`rank-row-${r.gymnastId}`}
                        >
                          <td className="px-3 py-2">
                            <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full text-sm font-bold ${overridden ? 'bg-amber-200 text-amber-900' : 'bg-gray-200 text-gray-700'}`}>
                              {r.rank != null ? r.rank : '—'}
                            </span>
                          </td>
                          <td className="px-3 py-2 font-medium">
                            {r.name}
                            {overridden && (
                              <span className="ml-2 text-[10px] uppercase text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded">
                                Override
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-gray-600">{r.club || '—'}</td>
                          <td className="px-3 py-2 text-right font-mono">
                            {score != null ? parseFloat(score).toFixed(3) : '—'}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <Input
                                value={edits[r.gymnastId] ?? ''}
                                onChange={(e) => handleEditChange(r.gymnastId, e.target.value)}
                                placeholder="rank"
                                className="h-9 w-20 text-center"
                                data-testid={`rank-input-${r.gymnastId}`}
                                disabled={isSaving}
                              />
                              <Button
                                size="sm"
                                onClick={() => handleSave(r)}
                                disabled={isSaving}
                                className="h-9 px-4 bg-black hover:bg-gray-800 text-white"
                                data-testid={`save-rank-${r.gymnastId}`}
                              >
                                {isSaving ? 'Saving…' : 'Save'}
                              </Button>
                              {overridden && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    handleEditChange(r.gymnastId, '');
                                    handleSave(r, '');
                                  }}
                                  disabled={isSaving}
                                  className="h-9 px-2 text-gray-500"
                                  title="Revert to automatic rank"
                                  data-testid={`revert-rank-${r.gymnastId}`}
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
